import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Link } from '../../../../../CommonComponents/OPL/Link/Link';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { useContactMutation } from '../../../../api/useContactMutation';
import { Textarea } from '../../../../../CommonComponents/OPL/Textarea/Textarea';
import { defaultValues, validation } from './ContactModal.validation';

interface ContactModalProps {
  onClose(): void;
  onBack(): void;
}

export const ContactModal: React.FC<ContactModalProps> = ({ onClose, onBack }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<{ message: string }>({
    defaultValues,
    resolver: yupResolver(validation),
  });

  const { mutateAsync: sendContact, isLoading, isSuccess, isError, error } = useContactMutation();

  return (
    <Modal size="medium" isOpen onRequestClose={onClose}>
      <Box
        as="form"
        onSubmit={handleSubmit(({ message }) => {
          sendContact(message).catch(() => {});
        })}
      >
        <ModalHeader>Contact</ModalHeader>

        <ModalCloseButton onClick={onClose} />

        <ModalBody>
          {isSuccess && (
            <Alert
              variant="success"
              message="Your message has been sent. Thank you for your feedback."
              mb="16"
            />
          )}

          {isError && <Alert variant="error" message={error?.message} mb="16" />}

          <Text as="span">
            Please tell us about any issues, suggestions, or questions. If you need any help, please
            check our&nbsp;
          </Text>

          <Link isExternal href={`${ADV.info.resourceCenterUrl}financial-planning`}>
            Resource Center page
          </Link>

          <Text as="span">, or email us at </Text>

          <Link isExternal href="mailto:financialplanning@orion.com">
            financialplanning@orion.com
          </Link>

          <Text as="span">&nbsp;or call (844) 223-8497.</Text>

          <Textarea
            {...register('message')}
            placeholder="Enter your request here."
            maxLength={1200}
            rows={4}
            disabled={isSuccess || isError}
            formVariant={errors.message ? 'error' : undefined}
            helperText={errors.message?.message}
            mt="16"
            aria-label="Enter Request"
          />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onBack} variant="secondary">
            Back
          </Button>

          <Button type="submit" isLoading={isLoading} disabled={isSuccess || isError} ml="8">
            Send Request
          </Button>
        </ModalFooter>
      </Box>
    </Modal>
  );
};
